// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    location: WarehouseModel;
    extendedStyles?: PickUpLocationAddressInfoDisplayStyles;
    cartPageLink?: string;
    isQuote?: boolean;
}

export interface PickUpLocationAddressInfoDisplayStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    addressGridItem?: GridItemProps;
    locationNameText?: TypographyPresentationProps;
    address?: AddressInfoDisplayStyles;
    iconProps?: IconProps;
}

export const pickUpLocationAddressInfoDisplayStyles: PickUpLocationAddressInfoDisplayStyles = {
    container: { gap: 0 },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            display: flex;
            flex-wrap: wrap;
            @media (max-width: 481px) {
                &&& {
                    margin-top: 20px;
                }
            }
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-right: 10px;
        `,
    },
    addressGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    locationNameText: {
        css: css`
            margin-bottom: -10px;
            font-weight: ${fontConfig.weight.bold};
        `,
    },
    editLink: {
        css: css`
            span {
                display: flex;
                align-items: center;
            }
            white-space: nowrap;
        `,
    },
    iconProps: {
        src: "ArrowRight",
        color: "primary",
        size: 17,
        css: css`
            margin-left: 3px;
        `,
    },
};

const PickUpLocationAddressInfoDisplay = ({ location, extendedStyles, cartPageLink, isQuote }: OwnProps) => {
    const styles = mergeToNew(pickUpLocationAddressInfoDisplayStyles, extendedStyles);
    return (
        <GridContainer {...styles.container} data-test-selector="pickUpLocationAddressInfoDisplay_container">
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.headingText} as="h3">
                    {translate("Pick Up Location")}
                </Typography>
                {!isQuote && (
                    <Link
                        {...styles.editLink}
                        href={cartPageLink}
                        data-test-selector="checkoutReviewAndSubmit_changeShipping"
                    >
                        {translate("Change Fulfillment Method to Delivery")}
                        <Icon {...styles.iconProps}></Icon>
                    </Link>
                )}
            </GridItem>
            <GridItem {...styles.addressGridItem}>
                <Typography {...styles.locationNameText}>{location.description || location.name}</Typography>
                <AddressInfoDisplay {...location} extendedStyles={styles.address} />
            </GridItem>
        </GridContainer>
    );
};

export default PickUpLocationAddressInfoDisplay;
